/**
 * Pure shaping for the card-identity columns of the RCT/TRC report.
 *
 * The SQL in getReceiptCardIdentityPg.ts does the masking — a card number is
 * reduced to its last four digits inside the database, so no longer value ever
 * reaches Node. What is left here is display formatting of fields that carry no
 * card number at all, kept free of server imports so it can be unit-tested.
 */

/** What one receipt row of the report shows about the card(s) it was paid with. */
export interface ReceiptCardIdentity {
    /** Last four digits only; several cards are joined as "1234, 5678". */
    last4: string | null;
    brand: string | null;
    /** MM/YY. */
    expiry: string | null;
    holderName: string | null;
    /** The card holder's ת.ז as typed on the payment page. */
    holderId: string | null;
    approvalNumber: string | null;
    payments: number | null;
    creditType: string | null;
    terminal: string | null;
    /** The Pelecard transaction id the receipt was charged under. */
    transactionRef: string | null;
}

/**
 * Card expiry in whatever form it was stored, as MM/YY.
 *
 *   "0527"                  Pelecard's CreditCardExpDate (MMYY) — native receipts
 *   "05/2027"               the galor import's format
 *   "05/27"                 already right
 *   "2027-05-31T00:00:00"   legacy clearanceCreditCard.cardExpirationDate
 *
 * Anything else comes back as-is rather than being guessed at.
 */
export const formatCardExpiry = (raw: unknown): string | null => {
    if (raw === null || raw === undefined) return null;
    const s = String(raw).trim();
    if (!s) return null;
    let m = s.match(/^(\d{2})(\d{2})$/);
    if (m) return `${m[1]}/${m[2]}`;
    m = s.match(/^(\d{2})\/(?:\d{2})?(\d{2})$/);
    if (m) return `${m[1]}/${m[2]}`;
    // Read the date part as text: a Date would shift an end-of-month timestamp
    // into the next month on a UTC container.
    m = s.match(/^\d{2}(\d{2})-(\d{2})-\d{2}/);
    if (m) return `${m[2]}/${m[1]}`;
    return s;
};

/** Pelecard `CreditType` (see docs/pelecard/parameters.md). */
const CREDIT_TYPES: Record<string, string> = {
    '1': 'Regular',
    '2': 'Isracredit',
    '3': 'Immediate Debit',
    '6': 'Credit',
    '8': 'Installments'
};

export const formatCreditType = (raw: unknown): string | null => {
    if (raw === null || raw === undefined || String(raw).trim() === '') return null;
    const key = String(Number(raw));
    return CREDIT_TYPES[key] ?? String(raw).trim();
};

const positiveInt = (raw: unknown): number | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : null;
};

/**
 * Number of payments on a card receipt, from the most authoritative source that
 * has one:
 *
 *   1. Pelecard's own TotalPayments for the charge
 *   2. the receipt's stored installmentPlan.count
 *   3. a legacy clearanceCreditCard.numOfPayments
 *   4. additionalPayments, "2 X 3566.68" meaning 2 more after the first
 *
 * `additionalPayments` is written as '' for a single charge by
 * buildReceiptCardDetails, so an empty string on a receipt that has a card
 * counts as one payment. Its absence says nothing.
 */
export const resolvePaymentsCount = (src: {
    totalPayments?: unknown;
    planCount?: unknown;
    clearanceCount?: unknown;
    additionalPayments?: unknown;
    hasCard?: boolean;
}): number | null => {
    const direct =
        positiveInt(src.totalPayments) ?? positiveInt(src.planCount) ?? positiveInt(src.clearanceCount);
    if (direct) return direct;
    if (typeof src.additionalPayments === 'string') {
        const m = src.additionalPayments.match(/^\s*(\d+)\s*[xX]/);
        if (m) return Number(m[1]) + 1;
        if (src.additionalPayments.trim() === '' && src.hasCard) return 1;
    }
    return null;
};

/** "Pegasus USD (0882577012)", or whichever half is known. */
export const formatTerminal = (number: unknown, name: unknown): string | null => {
    const num = number ? String(number).trim() : '';
    const nm = name ? String(name).trim() : '';
    if (num && nm) return `${nm} (${num})`;
    return num || nm || null;
};
